import React, { useEffect, useState, useRef } from 'react';
import { Calendar, Filter, X } from 'lucide-react';
import moment from 'moment';

const DateFilter = ({
  fromdate = '',
  todate = '',
  onDateChange,
  onApplyFilter,
  isLoading = false
}) => {
  // Accept either prop name safely
  const applyFilterFn = onApplyFilter ?? onDateChange;

  const [showCustomRange, setShowCustomRange] = useState(false);
  const [tempFromDate, setTempFromDate] = useState(fromdate);
  const [tempToDate, setTempToDate] = useState(todate);
  const [isResetting, setIsResetting] = useState(false);

  // Track previous props to avoid unnecessary sync
  const prevPropsRef = useRef({ fromdate, todate });

  /* ===================== SYNC TEMP STATE ===================== */

  useEffect(() => {
    if (
      prevPropsRef.current.fromdate !== fromdate ||
      prevPropsRef.current.todate !== todate
    ) {
      setTempFromDate(fromdate || '');
      setTempToDate(todate || '');
      prevPropsRef.current = { fromdate, todate };
    }
  }, [fromdate, todate]);

  /* ===================== HELPERS ===================== */

  // Always parse with strict mode
  const parseDate = (date) => moment(date, 'DD-MM-YYYY', true);

  const formatDateDisplay = (date) => {
    if (!date || typeof date !== 'string') return '--';
    const m = parseDate(date);
    return m.isValid() ? m.format('MMM DD, YYYY') : '--';
  };

  // Always get a fresh NOW at call time — never use stale memoized today
  const getNow = () => moment();

  const fmt = (m) => m.format('DD-MM-YYYY');

  const applyDates = (from, to) => {
    if (!from || !to) return;
    if (typeof from !== 'string' || typeof to !== 'string') return;

    setTempFromDate(from);
    setTempToDate(to);
    applyFilterFn?.({ from, to });
  };

  /* ===================== PRESET CONFIG ===================== */

  // Each preset defines exactly: how many units back, and what unit
  // end is ALWAYS today. All computed fresh at click time via getNow()
  const presetFilters = [
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'lastmonth', label: 'Last Month' },
    { value: 'last2month', label: 'Last 2 Months' },
    { value: 'last3month', label: 'Last 3 Months' },
    { value: 'last6month', label: 'Last 6 Months' },
    { value: 'lastyear', label: 'Last Year' },
  ];

  const quickRanges = [
    { days: 7, label: '7 Days' },
    { days: 30, label: '30 Days' },
    { days: 90, label: '90 Days' },
  ];

  /* ===================== HANDLERS ===================== */

  const handlePresetChange = (value) => {
    if (!value) return;

    // Fresh moment() at the exact time of click — never stale
    const now = getNow();
    const end = now.clone().endOf('day');
    let start;

    switch (value) {
      case 'last7days':
        // Today + 6 days back = 7 days total
        start = now.clone().subtract(6, 'days').startOf('day');
        break;

      case 'lastmonth':
        // Exactly 1 month back from today
        start = now.clone().subtract(1, 'months').startOf('day');
        break;

      case 'last2month':
        // Exactly 2 months back from today
        start = now.clone().subtract(2, 'months').startOf('day');
        break;

      case 'last3month':
        // Exactly 3 months back from today
        start = now.clone().subtract(3, 'months').startOf('day');
        break;

      case 'last6month':
        // Exactly 6 months back from today
        start = now.clone().subtract(6, 'months').startOf('day');
        break;

      case 'lastyear':
        // Exactly 1 year back from today
        start = now.clone().subtract(1, 'years').startOf('day');
        break;

      default:
        return;
    }

    applyDates(fmt(start), fmt(end));
    setShowCustomRange(false);
  };

  const handleQuickRange = (days) => {
    if (!days || days <= 0) return;

    // Fresh moment() at click time
    const now = getNow();
    const end = now.clone().endOf('day');
    // e.g. "7 Days" btn → subtract 7 days → gives 8-day window including today
    // Use (days - 1) so "7 Days" = exactly 7 days including today
    const start = now.clone().subtract(days - 1, 'days').startOf('day');

    applyDates(fmt(start), fmt(end));
    setShowCustomRange(false);
  };

  const handleCustomDateApply = () => {
    if (!tempFromDate || !tempToDate) return;

    const startMoment = parseDate(tempFromDate);
    const endMoment = parseDate(tempToDate);

    if (!startMoment.isValid() || !endMoment.isValid()) return;
    if (startMoment.isAfter(endMoment)) return;

    // Block future end dates
    if (endMoment.isAfter(getNow(), 'day')) return;

    applyDates(tempFromDate, tempToDate);
    setShowCustomRange(false);
  };

  const handleMainApply = () => {
    if (!showCustomRange && fromdate && todate) {
      applyDates(fromdate, todate);
    }
  };

  const handleReset = () => {
    if (isResetting || isLoading) return;
    try {
      setIsResetting(true);
      // Fresh now at reset time
      const today = fmt(getNow());
      applyDates(today, today);
      setShowCustomRange(false);
    } finally {
      setIsResetting(false);
    }
  };

  // ISO format for date input max/min attributes
  const todayISO = getNow().format('YYYY-MM-DD');

  /* ===================== UI ===================== */

  return (
    <div className='cusotm-date-fiulter'>
      <div className="modern-date-filter">
        <div className="filter-bar">
          <div className="filter-main">
            <div
              className="date-display"
              title={`${formatDateDisplay(fromdate)} - ${formatDateDisplay(todate)}`}
              onClick={() => {
                setTempFromDate(fromdate || '');
                setTempToDate(todate || '');
                setShowCustomRange(true);
              }}
              style={{width:'100%'}}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width:'100%' }}>
                <div style={{ fontSize: '10px', fontWeight: '500', color: '#64748b', whiteSpace: 'nowrap', flex: 1 }}>
                  {formatDateDisplay(fromdate)} - {formatDateDisplay(todate)}
                </div>
                <div>
                <Calendar className="calendar-icon" size={14} style={{ flexShrink: 0,width:'auto' }} />
                </div>
              </div>
            </div>

            <button
              className={`apply-btn ${isLoading ? 'loading' : ''}`}
              onClick={handleMainApply}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="spinner" />
              ) : (
                <>
                  <Filter size={16} />
                  <span>Apply Filter</span>
                </>
              )}
            </button>

            <button
              className="reset-btn-custom"
              onClick={handleReset}
              disabled={isLoading || isResetting}
              title="Reset to today's date"
            >
              {isResetting ? (
                <div className="spinner" />
              ) : (
                <>
                  <X size={16} />
                  <span>Reset</span>
                </>
              )}
            </button>
          </div>

          <div className="quick-ranges">
            {quickRanges.map((range) => (
              <button
                key={range.days}
                className="quick-range-btn"
                onClick={() => handleQuickRange(range.days)}
                disabled={isLoading}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {showCustomRange && (
          <div className="custom-range-popup">
            <div
              className="popup-overlay"
              onClick={() => setShowCustomRange(false)}
            />

            <div className="popup-content">
              <div className="popup-header">
                <h3>Select Date Range</h3>
                <button className="close-btn" onClick={() => setShowCustomRange(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="date-inputs">
                <div className="date-input-group">
                  <label>From Date</label>
                  <input
                    type="date"
                    value={
                      parseDate(tempFromDate).isValid()
                        ? parseDate(tempFromDate).format('YYYY-MM-DD')
                        : ''
                    }
                    onChange={(e) =>
                      setTempFromDate(
                        e.target.value ? moment(e.target.value).format('DD-MM-YYYY') : ''
                      )
                    }
                    max={
                      parseDate(tempToDate).isValid()
                        ? parseDate(tempToDate).format('YYYY-MM-DD')
                        : todayISO
                    }
                  />
                </div>

                <div className="date-input-group">
                  <label>To Date</label>
                  <input
                    type="date"
                    value={
                      parseDate(tempToDate).isValid()
                        ? parseDate(tempToDate).format('YYYY-MM-DD')
                        : ''
                    }
                    onChange={(e) =>
                      setTempToDate(
                        e.target.value ? moment(e.target.value).format('DD-MM-YYYY') : ''
                      )
                    }
                    min={
                      parseDate(tempFromDate).isValid()
                        ? parseDate(tempFromDate).format('YYYY-MM-DD')
                        : ''
                    }
                    max={todayISO}
                  />
                </div>
              </div>

              <div className="preset-filters">
                <label>Quick Select:</label>
                <div className="preset-grid">
                  {presetFilters.map((filter) => (
                    <button
                      key={filter.value}
                      className="preset-btn"
                      onClick={() => handlePresetChange(filter.value)}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="popup-actions">
                <button className="cancel-btn" onClick={() => setShowCustomRange(false)}>
                  Cancel
                </button>
                <button className="apply-custom-btn" onClick={handleCustomDateApply}>
                  Apply Custom Range
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateFilter;